import React, { useState, useEffect, useRef } from "react";
import {
  Loader2,
  AlertCircle,
  Bus,
  UtensilsCrossed,
  SlidersHorizontal,
  ChevronDown,
  X,
  Edit,
  Flag,
  Shield,
  ShieldCheck,
  ShieldX,
  Package,
  Phone,
  LogOut,
  Info,
} from "lucide-react";
import axios from "axios";
import CalendarInput from "./calendarInput";
import Pagination from "./Common/Pagination";
import defaultProfile from "../assets/profile.png";
import { useAuth } from "../context/auth-context";
import HandleEntry from "./Common/HandleEntry.jsx";
import HandleExit from "./Common/HandleExit.jsx";
import HandlePayment from "./Common/HandlePayment.jsx";
import detailsicon from "../assets/details.png"

// ── Outside-click hook ────────────────────────────────────────────────────────
const useOutsideClick = (callback) => {
  const ref = useRef();
  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) callback();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [callback]);
  return ref;
};
const ViewAttendanceAdmin = () => {
  // ── Auth (replaces manual token verification / localStorage lookups) ────────
  const { user, token } = useAuth();
  const isUTVMember = user?.UTVMember || false;
  const isUTV = isUTVMember ? "true" : "false";

  // ── Location / Company ────────────────────────────────────────────────────────
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // ── Filters ───────────────────────────────────────────────────────────────────
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [dateError, setDateError] = useState("");
  const [fetchError, setFetchError] = useState("");

  // ── Departments ───────────────────────────────────────────────────────────────
  const [allDepartments, setAllDepartments] = useState([]); // full objects (id + name)
  const [alldepartmentnames, setalldepartmentnames] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [isDeptOpenDesktop, setIsDeptOpenDesktop] = useState(false);
  const [isDeptOpenMobile, setIsDeptOpenMobile] = useState(false);

  // ── Payment status filter ─────────────────────────────────────────────────────
  const [selectedPaymentStatuses, setSelectedPaymentStatuses] = useState([]);
  const [isPaymentOpenDesktop, setIsPaymentOpenDesktop] = useState(false);
  const [isPaymentOpenMobile, setIsPaymentOpenMobile] = useState(false);
  const paymentOptions = ["Pending", "Success", "Payout Generated", "Failed"];
  const paymentStatusMap = {
    Pending: "pending",
    Success: "success",
    "Payout Generated": "payout_generated",
    Failed: "failed",
  };

  // ── Mobile filter panel (collapsed by default to save vertical space) ───────
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  // NEW: track which record's bank / verify tooltip is open (by attendanceId)
  // so tapping the icon works correctly per-row (not one shared boolean).
  const [showBankTooltipId, setShowBankTooltipId] = useState(null);
  const [showVerifyTooltipId, setShowVerifyTooltipId] = useState(null);

  // ── Table data ────────────────────────────────────────────────────────────────
  const [attendanceData, setAttendanceData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  // NEW: total working hours across the currently-fetched, filtered set,
  // sourced from pagination.totalWorkingHours in the API response.
  const [apiTotalWorkingHours, setApiTotalWorkingHours] = useState(0);
  const [showTable, setShowTable] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState({});

  // ── Photo modal ───────────────────────────────────────────────────────────────
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState("");
  const [isModalImageLoading, setIsModalImageLoading] = useState(false);

  // ── Entry / Exit edit modals ──────────────────────────────────────────────────
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showentrymarkdays, setshowentrymarkdays] = useState("");
  const [editLoadingId, setEditLoadingId] = useState(null); // attendanceId currently fetching worker-data

  // ── Payment ───────────────────────────────────────────────────────────────────
  const [paymentEditModal, setPaymentEditModal] = useState({ isOpen: false, record: null });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedAttendanceId, setSelectedAttendanceId] = useState(null);
  const [retryingPayments, setRetryingPayments] = useState({});

  // NEW: 3-dot "deduction details" modal (bus / food / essentials taken + amounts)
  const [deductionModal, setDeductionModal] = useState({ isOpen: false, record: null });

  // ── Worker-accepted (rejected) tooltip ────────────────────────────────────────
  const [workerAcceptedTooltip, setWorkerAcceptedTooltip] = useState({
    visible: false, content: "", position: { x: 0, y: 0 }, recordId: null,
  });

  // ── Payment reference tooltip ─────────────────────────────────────────────────
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipContent, setTooltipContent] = useState(null);
  const [activeTooltipId, setActiveTooltipId] = useState(null);
  const [isTooltipPinned, setIsTooltipPinned] = useState(false);

  // ── Accessory tooltip ─────────────────────────────────────────────────────────
  const [accessoryTooltip, setAccessoryTooltip] = useState({
    visible: false, content: null, position: { x: 0, y: 0 },
  });

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  // ── Date helper (used to auto-select "today" once a company is chosen) ──────
  const getTodayDateStr = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // ── Reset table state when location or company changes ────────────────────────
  useEffect(() => {
    setShowTable(false);
    setAttendanceData([]);
    setCurrentPage(1);
    setTotalPages(1);
    setTotalItems(0);
    setApiTotalWorkingHours(0);
    setSelectedDepartments([]);
    setSelectedDepartmentIds([]);
    setSelectedPaymentStatuses([]);
    setFetchError("");
    setDateError("");

    if (selectedCompany) {
      // Auto-select today's date so the list loads with a sensible default range
      const today = getTodayDateStr();
      setFromDate(today);
      setToDate(today);
      fetchAttendanceData(1, true, { startDate: today, endDate: today });
    } else {
      setFromDate("");
      setToDate("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, selectedCompany]);

  // ── Fetch locations on mount ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/lenskart/locations`, {
          headers: authHeaders,
        });
        if (response.data.success && Array.isArray(response.data.locations)) {
          setLocations(response.data.locations);
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };
    if (token) fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Fetch companies when location changes ─────────────────────────────────────
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!selectedLocation) { setCompanies([]); return; }
      try {
        setLoadingCompanies(true);
        const response = await axios.get(
          `${BASE_URL}/onDemandCompanies/list-companies/${selectedLocation}`,
          { headers: authHeaders }
        );
        if (response.data && Array.isArray(response.data.companies)) {
          setCompanies(response.data.companies);
        } else {
          setCompanies([]);
        }
      } catch {
        setCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, BASE_URL]);

  // Reset company when location changes
  useEffect(() => { setSelectedCompany(""); }, [selectedLocation]);

  // ── Dropdown refs ─────────────────────────────────────────────────────────────
  const locationDropdownRef = useOutsideClick(() => { if (isLocationOpen) setIsLocationOpen(false); });
  const companyDropdownRef = useOutsideClick(() => { if (isCompanyOpen) setIsCompanyOpen(false); });
  const departmentDropdownRef = useOutsideClick(() => { if (isDeptOpenDesktop) setIsDeptOpenDesktop(false); });
  const departmentDropdownRefMobile = useOutsideClick(() => { if (isDeptOpenMobile) setIsDeptOpenMobile(false); });
  const paymentDropdownRef = useOutsideClick(() => { if (isPaymentOpenDesktop) setIsPaymentOpenDesktop(false); });
  const paymentDropdownRefMobile = useOutsideClick(() => { if (isPaymentOpenMobile) setIsPaymentOpenMobile(false); });

  // ── Fetch departments when company changes ────────────────────────────────────
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.post(
          `${BASE_URL}/onDemandCompanies/list-departments`,
          { companyId: selectedCompany },
          { headers: authHeaders }
        );
        if (response.data.success && Array.isArray(response.data.departments)) {
          const departmentNames = response.data.departments
            .map((d) => d.department)
            .filter((d) => d && d !== "All");
          setalldepartmentnames(departmentNames);
          setAllDepartments(response.data.departments);
        } else {
          setalldepartmentnames([]);
          setAllDepartments([]);
        }
      } catch {
        setalldepartmentnames([]);
        setAllDepartments([]);
      }
    };
    if (selectedCompany) fetchDepartments();
    else {
      setalldepartmentnames([]);
      setAllDepartments([]);
      setSelectedDepartments([]);
      setSelectedDepartmentIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, BASE_URL]);

  const toggleDepartment = (dept) => {
    const deptObj = allDepartments?.find((d) => d.department === dept);
    setSelectedDepartments((prev) =>
      prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept]
    );
    setSelectedDepartmentIds((prev) => {
      const id = deptObj?._id;
      if (!id) return prev;
      return prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id];
    });
  };

  const handleSelectAllDepartments = () => {
    if (selectedDepartments.length === alldepartmentnames.length) {
      setSelectedDepartments([]);
      setSelectedDepartmentIds([]);
    } else {
      setSelectedDepartments([...alldepartmentnames]);
      setSelectedDepartmentIds(allDepartments?.map((d) => d._id).filter(Boolean) || []);
    }
  };

  // ── Payment status filter helpers ────────────────────────────────────────────
  const togglePaymentStatus = (status) => {
    setSelectedPaymentStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const handleSelectAllPayments = () => {
    setSelectedPaymentStatuses(
      selectedPaymentStatuses.length === paymentOptions.length ? [] : [...paymentOptions]
    );
  };

  // ── Fetch attendance data ─────────────────────────────────────────────────────
  // `overrides` lets callers (e.g. the auto-today-date effect) fetch with dates
  // that haven't been committed to state yet, avoiding a stale-closure fetch.
  const fetchAttendanceData = async (pageOrData, isNewSearch = false, overrides = {}) => {
    const page = typeof pageOrData === "object" ? currentPage : pageOrData;
    const effectiveFrom = overrides.startDate ?? fromDate;
    const effectiveTo = overrides.endDate ?? toDate;
    try {
      setLoading(true);
      setFetchError("");

      const params = { page, startDate: effectiveFrom, endDate: effectiveTo };
      if (selectedCompany) params.companyId = selectedCompany;
      if (selectedDepartments.length > 0) params.department = selectedDepartments.join(",");
      if (selectedDepartmentIds.length > 0) params.departmentId = selectedDepartmentIds.join(",");
      if (selectedPaymentStatuses.length > 0)
        params.payment = selectedPaymentStatuses.map((s) => paymentStatusMap[s]).join(",");

      const response = await axios.get(`${BASE_URL}/lenskart-admin/attendance/history`, {
        params,
        headers: authHeaders,
      });
      const result = response.data;
      if (result.success) {
        setAttendanceData(result.data);
        setShowTable(true);
        setTotalPages(result.pagination.totalPages || 1);
        setTotalItems(result.pagination.totalItems || result.data.length);
        // NEW: pull the aggregate working hours figure straight from the pagination block
        setApiTotalWorkingHours(result.pagination?.totalWorkingHours || 0);
        if (isNewSearch) setCurrentPage(1);
        setImageLoadingStates((prev) => {
          const newLoadingStates = {};
          result.data.forEach((record) => {
            if (record.profilePhotoUrl) {
              newLoadingStates[record.profilePhotoUrl] = prev[record.profilePhotoUrl] ?? true;
            }
          });
          return newLoadingStates;
        });
      } else {
        setFetchError(result.message || "Failed to fetch attendance data");
        setAttendanceData([]);
        setShowTable(false);
        setTotalPages(1);
        setTotalItems(0);
        setApiTotalWorkingHours(0);
      }
    } catch (error) {
      setFetchError(error.response?.data?.message || "Error fetching attendance data. Please try again.");
      setAttendanceData([]);
      setShowTable(false);
      setTotalPages(1);
      setTotalItems(0);
      setApiTotalWorkingHours(0);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (userId) => {
    if (activeTooltipId) {
      setActiveTooltipId(null);
      setTooltipVisible(false);
      setTooltipContent(null);
      setIsTooltipPinned(false);
      return;
    }
    const stateToSave = { fromDate, toDate, attendanceData, currentPage, showTable, selectedLocation, selectedCompany };
    localStorage.setItem("attendanceFilterState", JSON.stringify(stateToSave));
    window.open(`/lkart/admin/attendance-history/${userId}`, "_blank");
  };

  const handleEntryClick = async (record, e) => {
    e.stopPropagation();
    try {
      setEditLoadingId(record.attendanceId);
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/worker-data`,
        { attendanceId: record.attendanceId, workerId: record.userId },
        { headers: authHeaders }
      );
      if (response?.data?.success) {
        setSelectedRecord(response.data);
        setShowEntryModal(true);
        setshowentrymarkdays(response?.data?.EntryMarkedForPreviousDays || "7");
      }
    } catch (error) {
      console.error("Error preparing entry edit:", error);
    } finally {
      setEditLoadingId(null);
    }
  };

  const handleExitClick = async (record, e) => {
    e.stopPropagation();
    try {
      setEditLoadingId(record.attendanceId);
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/worker-data`,
        { attendanceId: record.attendanceId, workerId: record.userId },
        { headers: authHeaders }
      );
      if (response?.data?.success) {
        setSelectedRecord(response.data);
        setShowExitModal(true);
      }
    } catch (error) {
      console.error("Error preparing exit edit:", error);
    } finally {
      setEditLoadingId(null);
    }
  };
  const handlePending = (attendanceId, e) => {
    e.stopPropagation();
    setSelectedAttendanceId(attendanceId);
    setShowConfirmationModal(true);
  };

  // ── Payment reference tooltip ─────────────────────────────────────────────────
  const showTooltip = (e, record, isPinned = false) => {
    const rect = e.target.closest(".tooltip-trigger").getBoundingClientRect();
    setTooltipPosition({ x: rect.left + rect.width / 2 - 25, y: rect.top - 10 });
    setTooltipContent({
      referenceId: record.payment?.referenceId || "N/A",
      remarks: record.payment?.remark || record.payment?.transactionRemarks || "N/A",
    });
    setTooltipVisible(true);
    setActiveTooltipId(record.attendanceId);
    setIsTooltipPinned(isPinned);
  };

  const hideTooltip = () => {
    if (!isTooltipPinned) {
      setTooltipVisible(false);
      setTooltipContent(null);
      setActiveTooltipId(null);
    }
  };

  // ── Accessory tooltip ─────────────────────────────────────────────────────────
  const showAccessoryTooltip = (e, record) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setAccessoryTooltip({
      visible: true,
      content: {
        accessories: record.payment?.accessories || [],
        totalDeduction: record.ApronDeduction ?? record.totalDeductions ?? 0,
        totalPromotion: record?.payment?.bonusAmount || "",
      },
      position: { x: rect.left + rect.width / 2, y: rect.top },
    });
  };

  const hideAccessoryTooltip = () => {
    setAccessoryTooltip({ visible: false, content: null, position: { x: 0, y: 0 } });
  };

  // ── Worker-rejected tooltip ───────────────────────────────────────────────────
  const showWorkerAcceptedTooltip = (e, record) => {
    const rect = e.target.getBoundingClientRect();
    let content = "";
    if (record.WorkerAccepted === "pending") content = "Action pending";
    else if (record.WorkerAccepted === "rejected") content = record.rejectionReason || "Rejected";
    setWorkerAcceptedTooltip({
      visible: true, content,
      position: { x: rect.left + rect.width / 2, y: rect.top - 10 },
      recordId: record.attendanceId,
    });
  };

  const hideWorkerAcceptedTooltip = () => {
    setWorkerAcceptedTooltip({ visible: false, content: "", position: { x: 0, y: 0 }, recordId: null });
  };

  // ── Deduction details modal (3-dot icon) ──────────────────────────────────────
  // Shows: bus entry/exit deduction, food deduction, essentials (accessories)
  // taken with their allotted/deducted amounts, and the running total deducted.
  // Any leg (bus/food) with no deduction shows a muted/greyed-out icon instead
  // of the colored one, so it's obvious at a glance which services weren't used.
  const openDeductionModal = (record, e) => {
    if (e) e.stopPropagation();
    setDeductionModal({ isOpen: true, record });
  };

  const closeDeductionModal = () => setDeductionModal({ isOpen: false, record: null });

  // ── Validate dates ────────────────────────────────────────────────────────────
  const validateDates = () => {
    if (fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setDateError("From date cannot be later than To date");
        return false;
      }
      setDateError("");
      return true;
    }
    return false;
  };

  const handleApply = () => {
    if (validateDates()) {
      setFetchError("");
      fetchAttendanceData(1, true);
      setShowMobileFilters(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAttendanceData(page, false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Formatters ────────────────────────────────────────────────────────────────
  const formatDateToDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatTimeToIST = (isoString) => {
    if (!isoString) return "-";
    const timePart = isoString.split("T")[1].split(".")[0];
    const [hours, minutes] = timePart.split(":");
    let hour = parseInt(hours);
    const period = hour >= 12 ? "PM" : "AM";
    if (hour > 12) hour -= 12;
    else if (hour === 0) hour = 12;
    return `${hour}:${minutes} ${period}`;
  };

  // ── Verification icon helper ──────────────────────────────────────────────────
  const getAccountVerificationIcon = (status) => {
    if (status === "success") {
      return {
        icon: <ShieldCheck className="h-4 w-4 text-green-600" />,
        tooltip: "Account Verified",
        color: "text-green-600",
      };
    } else if (status === "failed") {
      return {
        icon: <ShieldX className="h-4 w-4 text-red-600" />,
        tooltip: "Account Not Verified",
        color: "text-red-600",
      };
    } else {
      return {
        icon: <Shield className="h-4 w-4 text-gray-400" />,
        tooltip: "Account Not Checked",
        color: "text-gray-400",
      };
    }
  };

  // ── Payment status color helper ───────────────────────────────────────────────
  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "success": return "bg-green-100 text-green-800";
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "payoutgenerated": return "bg-blue-100 text-blue-800";
      case "failed": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const activeFilterCount =
    (fromDate && toDate ? 1 : 0) +
    (selectedDepartments.length > 0 ? 1 : 0) +
    (selectedPaymentStatuses.length > 0 ? 1 : 0);

  const filterSummary = [
    fromDate && toDate ? `${formatDateToDisplay(fromDate)} – ${formatDateToDisplay(toDate)}` : null,
    selectedDepartments.length > 0
      ? (selectedDepartments.length === alldepartmentnames.length ? "All Departments" : selectedDepartments.join(", "))
      : null,
    selectedPaymentStatuses.length > 0
      ? (selectedPaymentStatuses.length === paymentOptions.length ? "All Payment Statuses" : selectedPaymentStatuses.join(", "))
      : null,
  ].filter(Boolean).join(" · ");

  // ── Page-level summary stats (from the currently loaded page of results) ────
  // NOTE: totalWorkingHoursThisPage/totalShiftsThisPage reflect only the records
  // currently loaded on this page. apiTotalWorkingHours + totalItems (below,
  // used in the summary bar) reflect the full filtered range from the API.
  const totalWorkingHoursThisPage = attendanceData
    .reduce((sum, r) => sum + (parseFloat(r?.workingHours) || 0), 0)
    .toFixed(2);
  const totalShiftsThisPage = attendanceData.length;

  const handleImageLoad = (photoUrl) => {
    setImageLoadingStates((prev) => ({ ...prev, [photoUrl]: false }));
  };

  const openPhotoModal = (photoUrl) => {
    setSelectedPhoto(photoUrl);
    setIsModalImageLoading(true);
    setShowPhotoModal(true);
  };

  const closePhotoModal = () => {
    setShowPhotoModal(false);
    setSelectedPhoto("");
    setIsModalImageLoading(false);
  };

  return (
    <div className="min-h-screen overflow-y-auto pt-16 bg-gray-100 p-0 z-2">
      <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-lg p-4 overflow-visible">
        <h2 className="text-2xl font-bold mb-4 text-center">View Attendance</h2>

        {/* ── Location & Company ── */}
        <div className="mb-4 grid grid-cols-2 gap-2 md:gap-4">
          {/* Location */}
          <div className="relative">
            <label className="text-xs md:text-sm text-gray-600 mb-1 block">
              Location <span className="text-red-500">*</span>
            </label>
            <div className="relative" ref={locationDropdownRef}>
              <button
                onClick={() => setIsLocationOpen(!isLocationOpen)}
                className="w-full px-2.5 md:px-3 py-2 text-xs md:text-sm border border-gray-300 rounded-md text-left flex justify-between items-center gap-1 bg-white"
              >
                <span className="truncate">{locations.find((l) => l._id === selectedLocation)?.name || "Select Location"}</span>
                <svg className={`h-4 w-4 flex-shrink-0 transform transition-transform ${isLocationOpen ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              {isLocationOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                  {locations.map((location, index) => (
                    <button
                      key={index}
                      onClick={() => { setSelectedLocation(location._id); setIsLocationOpen(false); }}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedLocation === location._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}
                    >
                      {location.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Company */}
          <div className="relative">
            <label className="text-xs md:text-sm text-gray-600 mb-1 block">
              Company <span className="text-red-500">*</span>
            </label>
            <div className="relative" ref={companyDropdownRef}>
              <button
                onClick={() => { if (selectedLocation && !loadingCompanies) setIsCompanyOpen(!isCompanyOpen); }}
                disabled={!selectedLocation || loadingCompanies}
                className={`w-full px-2.5 md:px-3 py-2 text-xs md:text-sm border border-gray-300 rounded-md text-left flex justify-between items-center gap-1 ${!selectedLocation || loadingCompanies ? "bg-gray-100 cursor-not-allowed text-gray-400" : "bg-white"}`}
              >
                <span className="truncate flex items-center">
                  {loadingCompanies
                    ? <span className="flex items-center"><Loader2 className="h-3 w-3 animate-spin mr-2" />Loading...</span>
                    : selectedCompany
                      ? companies.find((c) => c._id === selectedCompany)?.name || "Select Company"
                      : "Select Company"}
                </span>
                <svg className={`h-4 w-4 flex-shrink-0 transform transition-transform ${isCompanyOpen ? "rotate-180" : ""} ${!selectedLocation || loadingCompanies ? "text-gray-400" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              {isCompanyOpen && selectedLocation && !loadingCompanies && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                  {companies.length > 0 ? companies.map((company) => (
                    <button
                      key={company._id}
                      onClick={() => { setSelectedCompany(company._id); setIsCompanyOpen(false); }}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedCompany === company._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}
                    >
                      {company.name}
                    </button>
                  )) : <div className="px-4 py-2 text-sm text-gray-500">No companies found for this location</div>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Filters (only when company selected) ── */}
        {selectedCompany && (
          <div className="mb-4">
            {/* Desktop: single row, always visible */}
            <div className="hidden md:flex gap-2">
              <div className="flex-1">
                <label className="text-sm text-gray-600 mb-1 block">From Date <span className="text-red-500">*</span></label>
                <CalendarInput value={fromDate} onChange={(value) => { setFromDate(value); setDateError(""); }} className="px-2 py-1.5" />
              </div>
              <div className="flex-1">
                <label className="text-sm text-gray-600 mb-1 block">To Date <span className="text-red-500">*</span></label>
                <CalendarInput value={toDate} onChange={(value) => { setToDate(value); setDateError(""); }} className="px-2 py-1.5" />
              </div>
              <div className="flex-1 relative">
                <label className="text-sm text-gray-600 mb-1 block">Select Department</label>
                <div className="relative" ref={departmentDropdownRef}>
                  <button
                    onClick={() => setIsDeptOpenDesktop((p) => !p)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center"
                  >
                    {selectedDepartments.length === 0 ? "Select Department"
                      : selectedDepartments.length === alldepartmentnames.length ? "All Departments"
                      : selectedDepartments.length > 2 ? `${selectedDepartments.length} selected`
                      : selectedDepartments.join(", ")}
                    <svg className={`h-4 w-4 transform transition-transform ${isDeptOpenDesktop ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {isDeptOpenDesktop && (
                    <div className="absolute z-[9999] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                      <label className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-indigo-100">
                        <input type="checkbox" checked={selectedDepartments.length === alldepartmentnames.length && alldepartmentnames.length > 0} onChange={handleSelectAllDepartments} className="mr-2" />
                        Select All
                      </label>
                      {alldepartmentnames.map((dept, index) => (
                        <label key={index} className={`flex items-center px-4 py-2 text-sm hover:bg-indigo-100 ${selectedDepartments.includes(dept) ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}>
                          <input type="checkbox" checked={selectedDepartments.includes(dept)} onChange={() => toggleDepartment(dept)} className="mr-2" />
                          {dept}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1 relative">
                <label className="text-sm text-gray-600 mb-1 block">Select Payment Status</label>
                <div className="relative" ref={paymentDropdownRef}>
                  <button
                    onClick={() => setIsPaymentOpenDesktop((p) => !p)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center"
                  >
                    {selectedPaymentStatuses.length === 0 ? "Select Payment Status"
                      : selectedPaymentStatuses.length === paymentOptions.length ? "All Statuses"
                      : selectedPaymentStatuses.length > 2 ? `${selectedPaymentStatuses.length} selected`
                      : selectedPaymentStatuses.join(", ")}
                    <svg className={`h-4 w-4 transform transition-transform ${isPaymentOpenDesktop ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {isPaymentOpenDesktop && (
                    <div className="absolute z-[9999] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                      <label className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-indigo-100">
                        <input type="checkbox" checked={selectedPaymentStatuses.length === paymentOptions.length} onChange={handleSelectAllPayments} className="mr-2" />
                        Select All
                      </label>
                      {paymentOptions.map((status, index) => (
                        <label key={index} className={`flex items-center px-4 py-2 text-sm hover:bg-indigo-100 ${selectedPaymentStatuses.includes(status) ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}>
                          <input type="checkbox" checked={selectedPaymentStatuses.includes(status)} onChange={() => togglePaymentStatus(status)} className="mr-2" />
                          {status}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleApply}
                  disabled={!fromDate || !toDate || loading}
                  className="px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <span>Apply</span>}
                </button>
              </div>
            </div>

            {/* Mobile: compact stats bar + icon-only filter trigger */}
            <div className="md:hidden">
              <div className="flex items-center justify-end gap-2 mb-2">
                {showTable ? (
                  <></>
                ) : (
                  <div className="text-sm text-gray-400">{filterSummary || "Select a date range"}</div>
                )}

                <button
                  onClick={() => setShowMobileFilters((v) => !v)}
                  className="relative flex-shrink-0 h-9 w-9 flex items-center justify-center border border-gray-300 rounded-full bg-white text-gray-600"
                  title="Filters"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 min-w-[1rem] px-1 flex items-center justify-center text-[10px] bg-indigo-600 text-white rounded-full">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>

              {showMobileFilters && (
                <div className="mb-2 p-3 border border-gray-200 rounded-md bg-gray-50 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">From Date <span className="text-red-500">*</span></label>
                      <CalendarInput value={fromDate} onChange={(value) => { setFromDate(value); setDateError(""); }} className="px-2 py-1.5 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">To Date <span className="text-red-500">*</span></label>
                      <CalendarInput value={toDate} onChange={(value) => { setToDate(value); setDateError(""); }} className="px-2 py-1.5 text-sm" />
                    </div>
                  </div>

                  <div className="relative" ref={departmentDropdownRefMobile}>
                    <label className="text-xs text-gray-600 mb-1 block">Select Department</label>
                    <button
                      onClick={() => setIsDeptOpenMobile((p) => !p)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center bg-white"
                    >
                      {selectedDepartments.length === 0 ? "Select Department"
                        : selectedDepartments.length === alldepartmentnames.length ? "All Departments"
                        : selectedDepartments.length > 2 ? `${selectedDepartments.length} selected`
                        : selectedDepartments.join(", ")}
                      <svg className={`h-4 w-4 flex-shrink-0 transform transition-transform ${isDeptOpenMobile ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {isDeptOpenMobile && (
                      <div className="relative z-[9999] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                        <label className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-indigo-100">
                          <input type="checkbox" checked={selectedDepartments.length === alldepartmentnames.length && alldepartmentnames.length > 0} onChange={handleSelectAllDepartments} className="mr-2" />
                          Select All
                        </label>
                        {alldepartmentnames.map((dept, index) => (
                          <label key={index} className={`flex items-center px-4 py-2 text-sm hover:bg-indigo-100 ${selectedDepartments.includes(dept) ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}>
                            <input type="checkbox" checked={selectedDepartments.includes(dept)} onChange={() => toggleDepartment(dept)} className="mr-2" />
                            {dept}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="relative" ref={paymentDropdownRefMobile}>
                    <label className="text-xs text-gray-600 mb-1 block">Select Payment Status</label>
                    <button
                      onClick={() => setIsPaymentOpenMobile((p) => !p)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center bg-white"
                    >
                      {selectedPaymentStatuses.length === 0 ? "Select Payment Status"
                        : selectedPaymentStatuses.length === paymentOptions.length ? "All Statuses"
                        : selectedPaymentStatuses.length > 2 ? `${selectedPaymentStatuses.length} selected`
                        : selectedPaymentStatuses.join(", ")}
                      <svg className={`h-4 w-4 flex-shrink-0 transform transition-transform ${isPaymentOpenMobile ? "rotate-180" : ""}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {isPaymentOpenMobile && (
                      <div className="relative z-[9999] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                        <label className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-indigo-100">
                          <input type="checkbox" checked={selectedPaymentStatuses.length === paymentOptions.length} onChange={handleSelectAllPayments} className="mr-2" />
                          Select All
                        </label>
                        {paymentOptions.map((status, index) => (
                          <label key={index} className={`flex items-center px-4 py-2 text-sm hover:bg-indigo-100 ${selectedPaymentStatuses.includes(status) ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}>
                            <input type="checkbox" checked={selectedPaymentStatuses.includes(status)} onChange={() => togglePaymentStatus(status)} className="mr-2" />
                            {status}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {activeFilterCount > 0 && (
                      <button
                        onClick={() => {
                          setSelectedDepartments([]); setSelectedDepartmentIds([]);
                          setSelectedPaymentStatuses([]);
                          const today = getTodayDateStr();
                          setFromDate(today); setToDate(today);
                        }}
                        className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md flex items-center gap-1"
                      >
                        <X className="h-3.5 w-3.5" /> Reset
                      </button>
                    )}
                    <button
                      onClick={handleApply}
                      disabled={!fromDate || !toDate || loading}
                      className="flex-1 px-4 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <span>Apply</span>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {dateError && <div className="text-red-500 text-sm mb-2">{dateError}</div>}

        {fetchError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span className="text-sm">{fetchError}</span>
            </div>
          </div>
        )}

        {/* ── Desktop table ── */}
        {showTable && !fetchError && (
          <div className="hidden md:block w-full">
            <div className="max-h-[70vh] overflow-y-auto overflow-x-auto relative">
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 bg-white shadow-md z-[50] overflow-visible">
                  <tr className="bg-gray-100">
                    <th className="p-2 text-left">Name <br />(Worker Code)</th>
                    <th className="p-2 text-center">Department</th>
                    <th className="p-2 text-center">Entry Date <br />& Time</th>
                    <th className="p-2 text-center">Exit Date <br />& Time</th>
                    <th className="p-2 text-center">Payment <br />(Working Hr.)</th>
                   
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" className="text-center p-4"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></td></tr>
                  ) : attendanceData.length > 0 ? (
                    attendanceData.map((record) => {
                      const verification = getAccountVerificationIcon(record.nameVerificationStatus);
                      return (
                      <tr key={record.attendanceId} className="border-b hover:bg-gray-50 cursor-pointer">
                        {/* Name */}
                        <td className="p-2 text-left min-w-[160px]" onClick={() => handleRowClick(record.userId)}>
                          <div className="flex items-center">
                            {record.serialNumber && <div className="mr-3">{record.serialNumber}.</div>}

                            {/* Profile photo */}
                            <div className="relative w-12 h-12 flex-shrink-0">
                              {record.profilePhotoUrl && imageLoadingStates[record.profilePhotoUrl] && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-full">
                                  <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
                                </div>
                              )}
                              <img
                                src={record.profilePhotoUrl || defaultProfile}
                                alt="Profile"
                                className={`w-12 h-12 rounded-full object-fill cursor-pointer border border-gray-300 hover:opacity-80 transition-opacity ${
                                  record.profilePhotoUrl && imageLoadingStates[record.profilePhotoUrl] ? "opacity-0" : "opacity-100"
                                }`}
                                onClick={(e) => { e.stopPropagation(); openPhotoModal(record.profilePhotoUrl || defaultProfile); }}
                                onLoad={(e) => { if (e.target.complete && record.profilePhotoUrl) handleImageLoad(record.profilePhotoUrl); }}
                                onError={(e) => {
                                  e.target.src = defaultProfile;
                                  if (record.profilePhotoUrl) handleImageLoad(record.profilePhotoUrl);
                                }}
                              />
                            </div>

                            {/* Name + Verify Icon + Bank Alert + Call */}
                            <div className="ml-2 flex items-start gap-1">
                              <div className="relative">
                                <div className="font-medium leading-tight">{record.name || "N/A"}</div>
                                <div className="text-xs font-semibold text-gray-700 leading-tight mt-0.5">{record.workerCode || "N/A"}</div>
                              </div>

                              {/* Verify icon — click toggles its title/tooltip (works on click, not just hover) */}
<div className="relative mt-0.5">
  <span
    className="cursor-pointer"
    onClick={(e) => {
      e.stopPropagation();
      setShowBankTooltipId(null);
      setShowVerifyTooltipId((prev) =>
        prev === record.attendanceId ? null : record.attendanceId
      );
    }}
  >
    {verification.icon}
  </span>

  {showVerifyTooltipId === record.attendanceId && (
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50">
      <div className="bg-gray-800 text-white text-[11px] rounded-md shadow-lg px-2.5 py-1.5 w-[180px]">
        
        {/* Status */}
        <div className="font-semibold leading-tight">
          {verification.tooltip}
        </div>

        {/* Reason */}
        {record.nameVerificationStatus === "failed" &&
          record.nameVerificationRejectionReason && (
            <div className="mt-1 text-[10px] text-gray-300 leading-tight break-words">
              <span className="text-gray-400">Reason:</span>{" "}
              {record.nameVerificationRejectionReason}
            </div>
          )}
      </div>

      {/* Arrow */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
    </div>
  )}
</div>

                              {/* Bank account alert — click toggles its title/tooltip */}
                              {record?.haveAccount === false && (
                                <div className="relative">
                                  <AlertCircle
                                    className="w-4 h-4 text-red-500 flex-shrink-0 cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowVerifyTooltipId(null);
                                      setShowBankTooltipId((prev) => (prev === record.attendanceId ? null : record.attendanceId));
                                    }}
                                  />
                                  {showBankTooltipId === record.attendanceId && (
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                                      Bank account not added
                                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                                    </div>
                                  )}
                                </div>
                              )}




                              {/* Call icon */}
                              {record.phone && (
                                <div className="relative group/call mt-0.5">
                                  <Phone
                                    className="w-4 h-4 text-green-700 hover:text-green-800 cursor-pointer transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const cleanNumber = record.phone.replace(/[^\d+]/g, "");
                                      window.location.href = `tel:${cleanNumber}`;
                                    }}
                                  />
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 hidden group-hover/call:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap pointer-events-none">
                                    {record.phone}
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                                  </div>
                                </div>
                              )}

                              {/* Details icon (replaces the 3-dot) — opens the deduction details modal */}
                              <button
  className="mt-0.5 text-gray-400 hover:text-gray-700"
  onClick={(e) => openDeductionModal(record, e)}
  title="View deduction details"
>
  <img
    src={detailsicon}
    alt="Details"
    className="w-4 h-4 object-contain cursor-pointer"
  />
</button>
                            </div>
                          </div>
                        </td>

                        {/* Department */}
                        <td className="p-2 text-center" onClick={() => handleRowClick(record.userId)}>{record.department ? record.department : "_"}</td>

                        {/* Entry */}
                        <td className="p-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="flex flex-col items-center">
                              <div className="flex items-center justify-center gap-1">
                                <div className="font-medium">{formatDateToDisplay(record.entryDate)}</div>
                                {record.busEntry && <Bus className="h-4 w-4 text-[#3D5A80]" title="Used bus service for entry" />}
                              </div>
                              <span className="text-xs text-gray-600">{formatTimeToIST(record.entryTime)}</span>
                            </div>
                            {record.entryTime && (
                              editLoadingId === record.attendanceId ? (
                                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                              ) : (
                                <Edit
                                  className="h-4 w-4 text-black cursor-pointer hover:text-gray-700"
                                  onClick={(e) => handleEntryClick(record, e)}
                                  title="Modify Entry Time"
                                />
                              )
                            )}
                          </div>
                        </td>

                        {/* Exit */}
                        <td className="p-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="flex flex-col items-center">
                              <div className="flex items-center justify-center gap-1">
                                <div className="font-medium">{formatDateToDisplay(record.exitDate)}</div>
                                {record.exitTime && record.busExit && (
                                  <Bus className="h-4 w-4 text-[#3D5A80]" title="Used bus service for exit" />
                                )}
                              </div>
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-xs text-gray-600">{formatTimeToIST(record.exitTime)}</span>
                                {record.exitTime && record.haveFood && (
                                  <UtensilsCrossed className="h-4 w-4 text-[#F4A261]" title="Availed food service" />
                                )}
                              </div>
                            </div>
                            {record.exitTime ? (
                              editLoadingId === record.attendanceId ? (
                                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                              ) : (
                                <Edit
                                  className="h-4 w-4 text-black cursor-pointer hover:text-gray-700"
                                  onClick={(e) => handleExitClick(record, e)}
                                  title="Modify Exit Time"
                                />
                              )
                            ) : (
                              <LogOut
                                className="h-4 w-4 text-black cursor-pointer hover:text-gray-700"
                                onClick={(e) => handleExitClick(record, e)}
                                title="Mark Exit Time"
                              />
                            )}
                          </div>
                        </td>

                        {/* Payment */}
                        <td className="p-2 text-center">
                          <div className="flex flex-col items-center gap-1">
                            {/* Top line: amount + status/action icons */}
                            <div className="flex items-center justify-center w-auto mx-auto relative gap-1">
                              <span className={`px-2 py-0.5 text-lg ${getPaymentStatusColor(record.PaymentStatus)}`}>
                                ₹ {record.payment?.amount ?? "0.00"}
                              </span>

                              {
                                (record.PaymentStatus?.toLowerCase() === "failed" || record.PaymentStatus?.toLowerCase() === "payoutgenerated") && (
                                  <div className="relative tooltip-trigger" onClick={(e) => handlePending(record.attendanceId, e)}>
                                    {retryingPayments[record.attendanceId] ? (
                                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                    ) : (
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 cursor-pointer text-blue-600 hover:text-blue-800 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                      </svg>
                                    )}
                                  </div>
                                )}

                              {(record.PaymentStatus?.toLowerCase() === "success" ||
                                (record.PaymentStatus?.toLowerCase() === "failed" && !retryingPayments[record.attendanceId])) && (
                                <div
                                  className="relative tooltip-trigger"
                                  onMouseEnter={(e) => { if (!isTooltipPinned || activeTooltipId !== record.attendanceId) showTooltip(e, record, false); }}
                                  onMouseLeave={hideTooltip}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (activeTooltipId === record.attendanceId && isTooltipPinned) {
                                      setTooltipVisible(false); setTooltipContent(null);
                                      setActiveTooltipId(null); setIsTooltipPinned(false);
                                    } else {
                                      showTooltip(e, record, true);
                                    }
                                  }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 cursor-pointer transition-colors ${activeTooltipId === record.attendanceId && isTooltipPinned ? "text-blue-800" : "text-blue-600 hover:text-blue-800"}`}
                                    fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                              )}
                            </div>

                            {/* Bottom line: hrs + accessory indicator */}
                            <div className="flex items-center justify-center gap-1">
                              <span className="text-xs text-gray-500">{record?.workingHours || "0.00"} hrs</span>
                            </div>
                          </div>
                        </td>

                        {/* Details / 3-dot */}
                        {/* <td className="p-2 text-center">
                          <button
  className="mt-0.5 text-gray-400 hover:text-gray-700"
  onClick={(e) => openDeductionModal(record, e)}
  title="View deduction details"
>
  <img
    src={detailsicon}
    alt="Details"
    className="w-4 h-4 object-contain cursor-pointer"
  />
</button>
                        </td> */}
                      </tr>
                      );
                    })
                  ) : (
                    <tr><td colSpan="6" className="text-center p-4 text-gray-500">No records found for the selected date</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage} totalPages={totalPages}
                onPageChange={handlePageChange} totalItems={totalItems}
                activeColor="#4f46e5" inactiveColor="#f1f5f9" textColor="#4f46e5"
                containerStyle={{ borderTop: "1px solid #e5e7eb", marginTop: "0" }}
              />
            )}
          </div>
        )}

        {/* ── Mobile card list ── */}
{showTable && !fetchError && (
          <div className="md:hidden w-full">
            {loading ? (
              <div className="flex justify-center p-6"><Loader2 className="h-6 w-6 animate-spin text-gray-500" /></div>
            ) : attendanceData.length > 0 ? (
              <div className="space-y-3">
                {attendanceData.map((record) => {
                  const verification = getAccountVerificationIcon(record.nameVerificationStatus);
                  return (
                    <div
                      key={record.attendanceId}
                      onClick={() => handleRowClick(record.userId)}
                      className="bg-white border border-gray-100 rounded-2xl p-3 shadow-sm active:bg-gray-50"
                    >
                      {/* Header: photo + name/code/verify/alert + call */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center min-w-0">
                          <div className="flex-shrink-0 text-xs font-semibold text-gray-500 w-4 text-center mr-1">
                            {record.serialNumber}
                          </div>
                          <div className="relative w-12 h-12 flex-shrink-0">
                            {record.profilePhotoUrl && imageLoadingStates[record.profilePhotoUrl] && (
                              <div className="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-full">
                                <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
                              </div>
                            )}
                            <img
                              src={record.profilePhotoUrl || defaultProfile}
                              alt="Profile"
                              className={`w-12 h-12 rounded-full object-cover border border-gray-200 ${
                                record.profilePhotoUrl && imageLoadingStates[record.profilePhotoUrl] ? "opacity-0" : "opacity-100"
                              }`}
                              onClick={(e) => { e.stopPropagation(); openPhotoModal(record.profilePhotoUrl || defaultProfile); }}
                              onLoad={(e) => { if (e.target.complete && record.profilePhotoUrl) handleImageLoad(record.profilePhotoUrl); }}
                              onError={(e) => {
                                e.target.src = defaultProfile;
                                if (record.profilePhotoUrl) handleImageLoad(record.profilePhotoUrl);
                              }}
                            />
                          </div>
                          <div className="ml-2.5 min-w-0">
                            <div className="font-semibold text-[15px] text-gray-900 truncate">
                              {record.name || "N/A"}
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              <span className="text-xs text-gray-500">{record.workerCode || "N/A"}</span>

                              {/* Verification icon — click toggles its title/tooltip (works on touch, not just hover) */}
                              <div className="relative">
                                <span
                                  className="cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowBankTooltipId(null);
                                    setShowVerifyTooltipId((prev) => (prev === record.attendanceId ? null : record.attendanceId));
                                  }}
                                >
                                  {verification.icon}
                                </span>
{showVerifyTooltipId === record.attendanceId && (
  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50">
    <div className="bg-gray-800 text-white text-[11px] rounded-md shadow-lg px-2.5 py-1.5 w-[180px]">
      <div className="font-medium leading-tight">
        {verification.tooltip}
      </div>

      {record.nameVerificationRejectionReason && (
        <div className="mt-0.5 text-[10px] text-gray-300 leading-tight break-words whitespace-normal">
          <span className="text-gray-400">Reason:</span>{" "}
          {record.nameVerificationRejectionReason}
        </div>
      )}
    </div>

    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
  </div>
)}
                              </div>

                              {/* Bank account alert — click toggles its title/tooltip */}
                              {record?.haveAccount === false && (
                                <div className="relative">
                                  <AlertCircle
                                    className="w-4 h-4 text-red-500 flex-shrink-0 cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowVerifyTooltipId(null);
                                      setShowBankTooltipId((prev) => (prev === record.attendanceId ? null : record.attendanceId));
                                    }}
                                  />
                                  {showBankTooltipId === record.attendanceId && (
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                                      Bank account not added
                                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Details icon (replaces the 3-dot) — opens the deduction details modal */}
                               <button
  className="mt-0.5 text-gray-400 hover:text-gray-700"
  onClick={(e) => openDeductionModal(record, e)}
  title="View deduction details"
>
  <img
    src={detailsicon}
    alt="Details"
    className="w-4 h-4 object-contain cursor-pointer"
  />
</button>
                            </div>
                          </div>
                        </div>

                        {/* Call button */}
                        {record.phone && (
                          <a
                            href={`tel:${record.phone.replace(/[^\d+]/g, "")}`}
                            onClick={(e) => e.stopPropagation()}
                            className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-green-500 text-white shadow-sm ml-2"
                            title={record.phone}
                          >
                            <Phone className="h-4 w-4" fill="white" />
                          </a>
                        )}
                      </div>

                      {/* Payment row */}
                      <div className="grid grid-cols-[55%_45%] items-start mb-2" style={{ columnGap: "8px" }}>
                        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                          <span className={`px-2 py-0.5 rounded-full text-md font-medium ${getPaymentStatusColor(record.PaymentStatus)}`}>
                            ₹{record.payment?.amount ?? "0.00"}
                          </span>

                          {
                            (record.PaymentStatus?.toLowerCase() === "failed" || record.PaymentStatus?.toLowerCase() === "payoutgenerated") && (
                              <div onClick={(e) => handlePending(record.attendanceId, e)} title="Retry payment">
                                {retryingPayments[record.attendanceId] ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 cursor-pointer text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                )}
                              </div>
                            )}

                          {(record.PaymentStatus?.toLowerCase() === "success" ||
                            (record.PaymentStatus?.toLowerCase() === "failed" && !retryingPayments[record.attendanceId])) && (
                            <div
                              className="tooltip-trigger"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (activeTooltipId === record.attendanceId && isTooltipPinned) {
                                  setTooltipVisible(false); setTooltipContent(null);
                                  setActiveTooltipId(null); setIsTooltipPinned(false);
                                } else {
                                  showTooltip(e, record, true);
                                }
                              }}
                              title="Payment reference"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={`h-3.5 w-3.5 cursor-pointer transition-colors ${activeTooltipId === record.attendanceId && isTooltipPinned ? "text-blue-800" : "text-blue-600"}`}
                                fill="none" viewBox="0 0 24 24" stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          )}
                        </div>

                        <div className="text-left text-xs text-gray-800 min-w-0">
                          <div className="truncate">Department: <span className="text-gray-800">{record.department || "_"}</span></div>
                          <div>Working Hrs: <span className="text-gray-800">{record?.workingHours || "0.00"} Hr</span></div>
                        </div>
                      </div>

                      <div className="border-t border-gray-100 my-2" />

                      {/* Entry / Exit */}
                      <div className="grid grid-cols-[55%_45%] items-start" style={{ columnGap: "8px" }}>
                        <div className="text-xs">
                          <div className="text-gray-400">Entry Date &amp; Time</div>
                          <div className="font-medium text-gray-800 flex items-center gap-1 flex-wrap">
                            {formatDateToDisplay(record.entryDate)} | {formatTimeToIST(record.entryTime)}
                            {record.busEntry && <Bus className="h-3.5 w-3.5 text-[#3D5A80]" title="Bus - entry" />}
                            {record.entryTime && (
                              editLoadingId === record.attendanceId ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                              ) : (
                                <Edit
                                  className="h-3.5 w-3.5 text-indigo-600 cursor-pointer"
                                  onClick={(e) => handleEntryClick(record, e)}
                                  title="Modify Entry Time"
                                />
                              )
                            )}
                          </div>
                        </div>

                        <div className="text-xs text-left min-w-0">
                          <div className="text-gray-400">Exit Date &amp; Time</div>
                          <div className="font-medium text-gray-800 flex items-center gap-1 flex-wrap">
                            {record.exitTime ? (
                              <>
                                {formatDateToDisplay(record.exitDate)} | {formatTimeToIST(record.exitTime)}
                                {record.busExit && <Bus className="h-3.5 w-3.5 text-[#3D5A80]" title="Bus - exit" />}
                                {record.haveFood && <UtensilsCrossed className="h-3.5 w-3.5 text-[#F4A261]" title="Food" />}
                                {editLoadingId === record.attendanceId ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                                ) : (
                                  <Edit
                                    className="h-3.5 w-3.5 text-indigo-600 cursor-pointer"
                                    onClick={(e) => handleExitClick(record, e)}
                                    title="Modify Exit Time"
                                  />
                                )}
                              </>
                            ) : (
                              <LogOut
                                className="h-3.5 w-3.5 text-black cursor-pointer"
                                onClick={(e) => handleExitClick(record, e)}
                                title="Mark Exit Time"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center p-6 text-gray-500 text-sm">No records found for the selected date</div>
            )}

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage} totalPages={totalPages}
                onPageChange={handlePageChange} totalItems={totalItems}
                activeColor="#4f46e5" inactiveColor="#f1f5f9" textColor="#4f46e5"
                containerStyle={{ borderTop: "1px solid #e5e7eb", marginTop: "0" }}
              />
            )}
          </div>
        )}

        {/* ── Photo Modal ── */}
        {showPhotoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1000] p-4">
            <div className="relative max-w-md">
              <button onClick={closePhotoModal} className="absolute -top-4 -right-4 bg-white rounded-full p-2 hover:bg-gray-100 z-10">
                <span className="text-gray-600">✕</span>
              </button>
              <div className="relative">
                {isModalImageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-lg">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
                  </div>
                )}
                <img
                  src={selectedPhoto} alt="Profile"
                  className={`w-full h-auto rounded-lg max-h-[80vh] object-fill ${isModalImageLoading ? "opacity-0" : "opacity-100"}`}
                  onLoad={() => setIsModalImageLoading(false)}
                  onError={() => setIsModalImageLoading(false)}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── NEW: Deduction details modal (3-dot icon) ──────────────────────── */}
        {deductionModal.isOpen && deductionModal.record && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] p-4"
            onClick={closeDeductionModal}
          >
            <div
              className="bg-white rounded-2xl p-4 max-w-sm w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 text-sm">
                  {deductionModal.record.serialNumber ? `${deductionModal.record.serialNumber}. ` : ""}
                  {deductionModal.record.name || "N/A"} — Deduction Details
                </h3>
                <button onClick={closeDeductionModal}>
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-3 text-sm">
                {/* Bus Entry */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bus className={`h-4 w-4 ${deductionModal.record.busEntryDeduction > 0 ? "text-[#3D5A80]" : "text-gray-300"}`} />
                    <span className={deductionModal.record.busEntryDeduction > 0 ? "text-gray-800" : "text-gray-400"}>
                      Bus (Entry)
                    </span>
                  </div>
                  <span className={deductionModal.record.busEntryDeduction > 0 ? "font-medium text-red-600" : "text-gray-400"}>
                    {deductionModal.record.busEntryDeduction > 0
                      ? `- ₹${deductionModal.record.busEntryDeduction}`
                      : "Not used"}
                  </span>
                </div>

                {/* Bus Exit */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bus className={`h-4 w-4 ${deductionModal.record.busExitDeduction > 0 ? "text-[#3D5A80]" : "text-gray-300"}`} />
                    <span className={deductionModal.record.busExitDeduction > 0 ? "text-gray-800" : "text-gray-400"}>
                      Bus (Exit)
                    </span>
                  </div>
                  <span className={deductionModal.record.busExitDeduction > 0 ? "font-medium text-red-600" : "text-gray-400"}>
                    {deductionModal.record.busExitDeduction > 0
                      ? `- ₹${deductionModal.record.busExitDeduction}`
                      : "Not used"}
                  </span>
                </div>

                {/* Food */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UtensilsCrossed className={`h-4 w-4 ${deductionModal.record.foodDeduction > 0 ? "text-[#F4A261]" : "text-gray-300"}`} />
                    <span className={deductionModal.record.foodDeduction > 0 ? "text-gray-800" : "text-gray-400"}>
                      Food
                    </span>
                  </div>
                  <span className={deductionModal.record.foodDeduction > 0 ? "font-medium text-red-600" : "text-gray-400"}>
                    {deductionModal.record.foodDeduction > 0
                      ? `- ₹${deductionModal.record.foodDeduction}`
                      : "Not availed"}
                  </span>
                </div>

                {/* Essentials / accessories taken */}
                {deductionModal.record.payment?.accessories?.length > 0 ? (
                  <div className="pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Package className="h-3.5 w-3.5" /> Essentials Taken
                    </div>
                    {deductionModal.record.payment.accessories.map((acc, idx) => (
                      <div key={`${acc.accessoryId || acc.name}-${idx}`} className="flex items-center gap-2 py-1">
                        <img
                          src={acc.logo}
                          alt={acc.name}
                          className="h-4 w-4 rounded-full"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/fallback-accessory-icon.png";
                          }}
                        />
                        <span className="text-gray-800">{acc.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="pt-2 border-t border-gray-100 flex items-center gap-2 text-gray-400">
                    <Package className="h-4 w-4" />
                    <span>No essentials taken</span>
                  </div>
                )}

                {/* Total */}
                <div className="pt-2 border-t border-gray-200 flex items-center justify-between font-semibold">
                  <span>Total Deducted</span>
                  <span className="text-red-600">
                    ₹{deductionModal.record.totalDeductions ?? deductionModal.record.ApronDeduction ?? 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Payment reference tooltip ── */}
        {tooltipVisible && tooltipContent && (
          <div
            className={`fixed z-[9999] mr-4 p-3 text-xs bg-white shadow-xl rounded-lg border border-gray-200 max-w-[170px] transition-opacity duration-200 ${isTooltipPinned ? "pointer-events-auto" : "pointer-events-none"}`}
            style={{ left: tooltipPosition.x, top: tooltipPosition.y, transform: "translate(-50%, -100%)" }}
          >
            <div className="break-words">
              <strong className="text-gray-800">Reference ID :</strong>
              <span className="text-gray-600 ml-1">{tooltipContent.referenceId}</span>
            </div>
            <div className="break-words mt-2">
              <strong className="text-gray-800">Remarks :</strong>
              <span className="text-gray-600 ml-1">{tooltipContent.remarks}</span>
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-white"></div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-200 mt-[-1px]"></div>
          </div>
        )}


        {/* ── Worker-rejected tooltip ── */}
        {workerAcceptedTooltip.visible && workerAcceptedTooltip.content && (
          <div
            className="fixed z-[9999] p-3 text-xs bg-white rounded-lg border border-gray-200 max-w-[200px] transition-opacity duration-200"
            style={{ left: workerAcceptedTooltip.position.x, top: workerAcceptedTooltip.position.y, transform: "translate(-50%, -100%)" }}
          >
            <div className="break-words text-gray-800">{workerAcceptedTooltip.content}</div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-white"></div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-200 mt-[-1px]"></div>
          </div>
        )}

        {/* ── Accessory tooltip ── */}
        {accessoryTooltip.visible && accessoryTooltip.content && (
          <div
            className="fixed z-[9999] p-3 text-xs bg-white rounded-lg border border-gray-200 max-w-[220px]"
            style={{
              left: accessoryTooltip.position.x,
              top: accessoryTooltip.position.y,
              transform: "translate(-50%, -100%)",
            }}
          >
            <div className="space-y-1.5">
              {accessoryTooltip.content.accessories.map((acc) => (
                <div key={acc.accessoryId} className="flex items-center gap-2">
                  <img
                    src={acc.logo}
                    alt={acc.name}
                    className="h-4 w-4 rounded-full"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/fallback-accessory-icon.png";
                    }}
                  />
                  <span className="text-gray-800">{acc.name}</span>
                </div>
              ))}
              <div className="pt-1 border-t border-gray-100 font-medium text-gray-900">
                {accessoryTooltip.content.totalDeduction === 0
                  ? `Total Refund Amount: ₹${accessoryTooltip.content.totalPromotion ?? 0}`
                  : `Total Deduction: ₹${accessoryTooltip.content.totalDeduction}`}
              </div>
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-white"></div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-200 mt-[-1px]"></div>
          </div>
        )}

        {/* ── Entry edit modal ── */}
        <HandleEntry
          showEntryModal={showEntryModal}
          setShowEntryModal={setShowEntryModal}
          selectedRecord={selectedRecord}
          selectedCompany={selectedCompany}
          currentPage={currentPage}
          fetchAttendanceData={fetchAttendanceData}
          showentrymarkdays={showentrymarkdays}
          setshowentrymarkdays={setshowentrymarkdays}
        />

               {/* ── Exit edit modal ── */}
        <HandleExit
          showExitModal={showExitModal}
          setShowExitModal={setShowExitModal}
          selectedRecord={selectedRecord}
          selectedCompany={selectedCompany}
          isUTVMember={isUTVMember}
          currentPage={currentPage}
          fetchAttendanceData={fetchAttendanceData}
        />

        {/* ── Payment retry / confirmation modal ── */}
        <HandlePayment
          paymentEditModal={paymentEditModal}
          setPaymentEditModal={setPaymentEditModal}
          showConfirmationModal={showConfirmationModal}
          setShowConfirmationModal={setShowConfirmationModal}
          selectedAttendanceId={selectedAttendanceId}
          setSelectedAttendanceId={setSelectedAttendanceId}
          retryingPayments={retryingPayments}
          setRetryingPayments={setRetryingPayments}
          currentPage={currentPage}
          fetchAttendanceData={fetchAttendanceData}
          setAttendanceData={setAttendanceData}
        />
      </div>
    </div>
  );
};

export default ViewAttendanceAdmin;